"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useState } from "react";
import { twMerge } from "tailwind-merge";

import { BREAKPOINTS, formatPrice, getLinkTarget, VARIANTS } from "@/globals";
import { BUTTON_STYLES } from "@/styles/buttons";
import { TYPO_STYLES } from "@/styles/typography";
import { WRAPPER_STYLES } from "@/styles/wrapper";

export type ProductShoppingCardProps = {
    benefits?: string[];
    buyAbo?: {
        label: string;
        price: number;
        primaryButton: {
            href: string;
            label: string;
        };
        secondaryButton: {
            href: string;
            label: string;
        };
        trialSubscription?: string;
        trialSubscriptionDescription?: string;
    };
    buyProduct?: {
        label: string;
        price: number;
        primaryButton: {
            href: string;
            label: string;
        };
    };
    image?: {
        description: string;
        size: {
            height: number;
            width: number;
        };
        src: string;
    };

    priceInfo?: string;
    singleDownloadButton?: string;
    variant?: VARIANTS;
};

const TryAboView = ({ buyAbo, priceInfo, benefits, variant }: ProductShoppingCardProps) => {
    if (buyAbo) {
        return (
            <>
                <h3 className={twMerge(TYPO_STYLES.headlineExtraBold5(), "text-greyWireframe")}>
                    {buyAbo.trialSubscription}
                </h3>
                <p className={twMerge(TYPO_STYLES.bodyNormal18(), "text-greyWireframe")}>
                    {buyAbo.trialSubscriptionDescription}
                </p>
                <Link
                    className={twMerge(BUTTON_STYLES.primaryBig(), "w-full")}
                    href={buyAbo.primaryButton.href}
                    prefetch={false}
                >
                    <span>{buyAbo.primaryButton.label}</span>
                    <span className={TYPO_STYLES.icon()}>
                        {String.fromCharCode(Number`0xe979`)}
                    </span>
                </Link>
                {variant === VARIANTS.a && (
                    <div className="-mx-8 p-8 bg-yellowSoft flex flex-col gap-4">
                        <ul className="product-abo-check flex flex-col gap-2">
                            {benefits?.map((benefit, i) => {
                                return (
                                    <li key={i} className="flex items-start gap-3">
                                        {benefit}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
                <p className={TYPO_STYLES.bodyNormal14()}>{priceInfo}</p>
            </>
        );
    } else return null;
};

const BuyAboView = ({ buyAbo }: ProductShoppingCardProps) => {
    if (buyAbo) {
        return (
            <>
                <h3 className={twMerge(TYPO_STYLES.headlineExtraBold5(), "text-greyWireframe")}>
                    {buyAbo.label}
                </h3>
                <h4 className={twMerge(TYPO_STYLES.headlineExtraBold4(), "text-greyWireframe")}>
                    {formatPrice(buyAbo.price)} *
                </h4>
                <Link
                    className={twMerge(BUTTON_STYLES.secondaryBig(), "w-full")}
                    target={getLinkTarget(buyAbo.secondaryButton.href)}
                    href={buyAbo.secondaryButton.href}
                    prefetch={false}
                >
                    <span>{buyAbo.secondaryButton.label}</span>
                    <span className={twMerge(TYPO_STYLES.icon(), "text-2xl")}>
                        {String.fromCharCode(Number`0xe938`)}
                    </span>
                </Link>
            </>
        );
    } else return null;
};

const BuyProductView = ({ buyProduct }: ProductShoppingCardProps) => {
    if (buyProduct) {
        return (
            <>
                <h3 className={twMerge(TYPO_STYLES.headlineExtraBold5(), "text-greyWireframe")}>
                    {buyProduct.label}
                </h3>
                <h4 className={twMerge(TYPO_STYLES.headlineExtraBold4(), "text-greyWireframe")}>
                    {formatPrice(buyProduct.price)}
                </h4>
                <Link
                    className={twMerge(BUTTON_STYLES.primaryBig(), "w-full")}
                    href={buyProduct.primaryButton.href}
                    target={getLinkTarget(buyProduct.primaryButton.href)}
                    prefetch={false}
                >
                    <span> {buyProduct.primaryButton.label}</span>
                    <span className={TYPO_STYLES.icon()}>
                        {String.fromCharCode(Number`0xe979`)}
                    </span>
                </Link>
            </>
        );
    } else return null;
};

export const ProductShoppingCardView = ({
    priceInfo,
    image,
    buyProduct,
    buyAbo,
    variant,
    benefits,

    singleDownloadButton,
}: ProductShoppingCardProps) => {
    const [openPopup, setOpenPopup] = useState(false);

    return (
        <>
            {buyAbo && (
                <div
                    className={twMerge(
                        "hidden p-8 lg:block min-w-[290px] w-full mx-auto rounded-lg bg-white",
                    )}
                >
                    <div className="flex gap-6 flex-col">
                        <BuyAboView buyAbo={buyAbo} priceInfo={priceInfo} image={image} />
                    </div>
                </div>
            )}
            {buyAbo && (
                <div
                    className={twMerge(
                        "hidden p-8 lg:block min-w-[290px] w-full mx-auto rounded-lg bg-white",
                    )}
                >
                    <div className="flex gap-6 flex-col">
                        <TryAboView
                            buyAbo={buyAbo}
                            priceInfo={priceInfo}
                            image={image}
                            benefits={benefits}
                            variant={variant}
                        />
                        {image?.src && (
                            <Image
                                src={image.src}
                                {...image.size}
                                alt={image.description}
                                style={{ height: "auto", width: "100%", objectFit: "cover" }}
                                sizes={`(min-width: ${BREAKPOINTS.sm}px) ${BREAKPOINTS.sm}px, 100vw`}
                            />
                        )}
                        {variant === VARIANTS.a &&
                            buyProduct?.primaryButton.href &&
                            singleDownloadButton && (
                                <Link
                                    href={buyProduct.primaryButton.href}
                                    target={getLinkTarget(buyProduct.primaryButton.href)}
                                    prefetch={false}
                                    className={BUTTON_STYLES.secondaryBig()}
                                >
                                    {singleDownloadButton}
                                </Link>
                            )}
                    </div>
                </div>
            )}
            {((variant === VARIANTS.a && !buyAbo && buyProduct) ||
                (variant === VARIANTS.b && buyProduct)) && (
                <div
                    className={twMerge(
                        "hidden p-8 lg:block min-w-[290px] mx-auto w-full rounded-lg bg-white",
                    )}
                >
                    <div className="flex gap-6 flex-col">
                        <BuyProductView buyProduct={buyProduct} />
                        {image?.src && (
                            <Image
                                src={image.src}
                                {...image.size}
                                alt={image.description}
                                style={{ height: "auto", width: "100%", objectFit: "cover" }}
                                sizes={`(min-width: ${BREAKPOINTS.sm}px) ${BREAKPOINTS.sm}px, 100vw`}
                            />
                        )}
                    </div>
                </div>
            )}
            {openPopup && (
                <div className="fixed lg:hidden h-dvh w-full top-0 left-0 bg-modalBackground z-50 flex items-center justify-center p-6">
                    <div className="bg-grey1 rounded-3xl p-4 pt-14 relative ">
                        <div className="overflow-y-scroll overflow-x-hidden max-h-[80vh] flex flex-col gap-4">
                            <button
                                className={twMerge(
                                    TYPO_STYLES.icon(),
                                    "text-2xl right-4 top-4 absolute",
                                )}
                                onClick={() => setOpenPopup(false)}
                            >
                                {String.fromCharCode(Number`0xe91b`)}
                            </button>
                            <div className="flex flex-col gap-4">
                                {buyAbo && (
                                    <div
                                        className={twMerge(
                                            "p-6 lg:hidden min-w-[290px] w-full mx-auto rounded-lg bg-white",
                                        )}
                                    >
                                        <div className="flex gap-6 flex-col">
                                            <BuyAboView
                                                buyAbo={buyAbo}
                                                priceInfo={priceInfo}
                                                image={image}
                                            />
                                        </div>
                                    </div>
                                )}
                                {buyAbo && (
                                    <div
                                        className={twMerge(
                                            "p-6 lg:hidden min-w-[290px] w-full mx-auto rounded-lg bg-white",
                                        )}
                                    >
                                        <div className="flex gap-4 flex-col">
                                            <TryAboView
                                                buyAbo={buyAbo}
                                                priceInfo={priceInfo}
                                                image={image}
                                                benefits={benefits}
                                                variant={variant}
                                            />
                                        </div>
                                    </div>
                                )}

                                {((variant === VARIANTS.a && !buyAbo && buyProduct) ||
                                    (variant === VARIANTS.b && buyProduct)) && (
                                    <div
                                        className={twMerge(
                                            "p-6 lg:hidden min-w-[290px] mx-auto w-full rounded-lg bg-white",
                                        )}
                                    >
                                        <div className="flex gap-4 flex-col">
                                            <BuyProductView buyProduct={buyProduct} image={image} />
                                        </div>
                                    </div>
                                )}
                            </div>
                            {variant === VARIANTS.a &&
                                buyAbo &&
                                buyProduct?.primaryButton.href &&
                                singleDownloadButton && (
                                    <Link
                                        href={buyProduct.primaryButton.href}
                                        target={getLinkTarget(buyProduct.primaryButton.href)}
                                        prefetch={false}
                                        className={BUTTON_STYLES.secondaryBig()}
                                    >
                                        {singleDownloadButton}
                                    </Link>
                                )}
                            {image?.src && (
                                <Image
                                    src={image.src}
                                    {...image.size}
                                    alt={image.description}
                                    style={{ height: "auto", width: "100%", objectFit: "cover" }}
                                    sizes={`(min-width: ${BREAKPOINTS.sm}px) ${BREAKPOINTS.sm}px, 100vw`}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
            <div className="fixed lg:hidden bottom-0 left-0 bg-brandDark rounded-lg py-2 pl-6 pr-2 w-full flex justify-end items-center gap-6">
                <h4
                    className={twMerge(
                        TYPO_STYLES.headlineExtraBold4(),
                        WRAPPER_STYLES.HCenterLeft(),
                        "text-white gap-4",
                    )}
                >
                    <span>
                        {buyAbo?.price || buyAbo?.price === 0
                            ? formatPrice(buyAbo?.price)
                            : formatPrice(buyProduct?.price)}
                    </span>
                </h4>
                <button
                    className={twMerge(BUTTON_STYLES.primary())}
                    onClick={() => setOpenPopup(true)}
                >
                    <span className={twMerge(TYPO_STYLES.icon(), "text-2xl")}>
                        {String.fromCharCode(Number`0xe979`)}
                    </span>
                </button>
            </div>
        </>
    );
};
